import { Box, Skeleton, Typography } from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import dayjs, { Dayjs } from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import { useLanguage, useTenant } from "hooks/settings";
import { Loaded } from "models/loaded";
import { CompliancePodium } from "components/strategyDashboard/compliance/CompliancePodium";
import { useCompliancePodiumScores } from "components/strategyDashboard/compliance/complianceHooks";
import { CompliancePodiumScores } from "components/strategyDashboard/compliance/utils";
import { OverviewCard } from "./OverviewCard";

export const Compliance = ({ date }: { date: Dayjs }) => {
  const { t } = useTenantTranslation();
  const tenant = useTenant();
  const language = useLanguage();
  dayjs.locale(language);
  const monthName = date.format("MMMM");

  const loadedScores = useCompliancePodiumScores(
    date.startOf("month").format(),
    date.endOf("day").format()
  );

  return (
    <OverviewCard
      title={t("monthCompliance", { month: monthName })}
      icon={EmojiEventsIcon}
      buttonText={t("viewCompliance")}
      buttonHref={`/${tenant}/dashboard/compliance`}
    >
      <ComplianceContent loadedScores={loadedScores} />
    </OverviewCard>
  );
};

const ComplianceContent = ({
  loadedScores,
}: {
  loadedScores: Loaded<CompliancePodiumScores>;
}) => {
  const { t } = useTenantTranslation();

  const isFetching =
    loadedScores.status === "success" ? loadedScores.fetching : false;
  const isLoading =
    loadedScores.status === "loading" || loadedScores.status === "waiting";

  if (isLoading || isFetching) {
    return <Skeleton variant="rectangular" height={180} sx={{ m: 2 }} />;
  }

  if (
    loadedScores.status !== "success" ||
    (loadedScores.data.overallCompliancePercent === undefined &&
      loadedScores.data.crewScores.length === 0)
  ) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          {t("noData")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        pb: 3,
      }}
    >
      <CompliancePodium
        embedded
        crewScores={loadedScores.data.crewScores}
        overallCompliancePercent={loadedScores.data.overallCompliancePercent}
      />
    </Box>
  );
};
